import { Divider, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api/index"
import { queries } from "api/queries"
import PageContainer from "components/PageContainer"
import TabBar from "components/TabBar"
import useTabs, { Tabs } from "components/TabBar/hooks/useTabs"
import { useSuraasaPlusNewYear } from "features/Home/SuraasaPlusNewYearOnboarding/Context"
import MasterclassOnboarding from "features/Home/SuraasaPlusNewYearOnboarding/MasterclassOnboarding"
import { MASTERCLASS_ONBOARDING_TARGETS } from "features/Home/SuraasaPlusNewYearOnboarding/MasterclassOnboarding/types"
import UpcomingMasterclass from "features/Masterclasses/UpcomingMasterclass"
import Masterclasses from "features/MyCertificates/Masterclasses"
import { useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import ExploreMasterclasses from "./ExploreMasterclasses"

export enum MasterclassTabs {
  explore = "Explore",
  myCertificates = "My Certificates",
}

const initialTabs: Tabs = [
  {
    name: MasterclassTabs.explore,
    content: () => null,
    count: 0,
  },
  {
    name: MasterclassTabs.myCertificates,
    content: () => null,
    count: 0,
  },
]

const MasterclassesPage = () => {
  const [tabs] = useState(initialTabs)
  const [activeTab, setActiveTab] = useTabs({ tabs, initialTab: tabs[0].name })

  const [isOnboardingActive, setIsOnboardingActive] = useState(false)
  const { isSpnyPlatformIntroCompleted } = useSuraasaPlusNewYear()

  // Track page view on mount
  useEffect(() => {
    trackingService.trackEvent(
      TrackingEvents.masterclassesPageViewed,
      {
        active_tab: activeTab.name,
      },
      { services: ["PostHog"] }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const pageTitle =
    activeTab.name === MasterclassTabs.explore
      ? "All Masterclasses"
      : "My Certificates"

  const masterclassCertificates = useQuery({
    queryKey: queries.profile.masterclassCertificatesList().queryKey,
    queryFn: () =>
      api.profile.credentials.list({
        params: {
          types: ["masterclass"],
        },
      }),
  })

  const upComingMasterclassQuery = useQuery({
    queryKey: queries.masterclasses.upcomingMasterclass().queryKey,
    queryFn: () => api.masterclasses.overview(),
    enabled: isOnboardingActive,
  })

  return (
    <>
      {/* @ts-expect-error react version issues */}
      <Helmet title={pageTitle}>
        <title>{pageTitle}</title>
      </Helmet>
      <div className="bg-surface">
        <PageContainer>
          <div className="my-2">
            <Typography variant="title2">Masterclasses</Typography>
          </div>
        </PageContainer>
        <PageContainer>
          <TabBar
            className="pt-1"
            activeTab={activeTab}
            tabs={tabs}
            onChange={tab => {
              setActiveTab(tab)
              trackingService.trackEvent(
                TrackingEvents.masterclassesTabChanged,
                {
                  from_tab: activeTab.name,
                  to_tab: tab,
                },
                { services: ["PostHog"] }
              )
            }}
            getOnboardingTarget={tab => {
              if (tab.name === MasterclassTabs.explore)
                return MASTERCLASS_ONBOARDING_TARGETS.explore

              if (tab.name === MasterclassTabs.myCertificates)
                return MASTERCLASS_ONBOARDING_TARGETS.certificate

              return undefined
            }}
          />
        </PageContainer>
        <Divider className="shadow-[0px_6px_15px_rgba(0,0,0,0.05)]] -mt-px  h-[1.75px]" />

        {activeTab.name === MasterclassTabs.explore && (
          <div className="mt-4 shadow-[0_4px_4px_-1px_rgba(0,0,0,0.1)] sm:mt-5">
            <PageContainer>
              <UpcomingMasterclass
                renderWithTitle
                isOnboardingActive={isOnboardingActive}
              />
            </PageContainer>
          </div>
        )}
      </div>
      {activeTab.name === MasterclassTabs.explore && <ExploreMasterclasses />}
      {activeTab.name === MasterclassTabs.myCertificates && (
        <PageContainer className="mt-5">
          <Masterclasses masterclassCertificates={masterclassCertificates} />
        </PageContainer>
      )}
      {isSpnyPlatformIntroCompleted && (
        <MasterclassOnboarding
          onStart={() => {
            setIsOnboardingActive(true)
            trackingService.trackEvent(
              TrackingEvents.masterclassOnboardingStarted,
              {},
              { services: ["PostHog"] }
            )
          }}
          onEnd={() => {
            setIsOnboardingActive(false)
            setActiveTab(MasterclassTabs.explore)
            trackingService.trackEvent(
              TrackingEvents.masterclassOnboardingCompleted,
              {},
              { services: ["PostHog"] }
            )
          }}
          setActiveTab={setActiveTab}
          hasUpcomingMasterclass={!!upComingMasterclassQuery.data}
          // hasUpcomingMasterclass={false}
          isLoading={
            upComingMasterclassQuery.isLoading ||
            masterclassCertificates.isLoading
          }
        />
      )}
    </>
  )
}

export default MasterclassesPage
